import axios from 'axios'
import api from './api'
import { locationService } from './locationService'

// Note: Use 'api' (with token interceptor) instead of 'axios' directly

const API_URL = process.env.VUE_APP_API_URL || 'https://empirecanada.ca/api';

// Log to check whether API_URL is defined
console.log('API_URL no productService:', API_URL)

/**
 * Removes items whose displayed price (already dynamic from the API) is outside [minPrice, maxPrice].
 * Keeps LPOR, price-on-request (-1) or invalid (not comparable to the range).
 */
export function filterProductsByDisplayedPriceRange(products, minPrice, maxPrice) {
  if (!Array.isArray(products)) return []
  const minN = Number(minPrice)
  const maxN = Number(maxPrice)
  if (!Number.isFinite(minN) || !Number.isFinite(maxN)) {
    return products.slice()
  }

  return products.filter((p) => {
    if (p.lpor === true) return true
    const raw = p.price
    const price = typeof raw === 'string' ? parseFloat(raw) : Number(raw)
    if (!Number.isFinite(price) || price < 0) return true
    return price >= minN && price <= maxN
  })
}

export const productService = {
  async getProducts(filters = {}) {
    const {
      categoryId,
      categoryIds,
      brands,
      minPrice,
      maxPrice,
      page = 1,
      limit = 8,
      sortBy = 'featured',
      search,  // Text search parameter
      filters: dynamicFilters,  // Dynamic filters parameter (characteristics)
      inStockOnly,
      locationId  // Branch ID for dynamic price calculation
    } = filters

    // If locationId was not provided, try to obtain it from sessionStorage
    let finalLocationId = locationId
    if (!finalLocationId) {
      try {
        const selectedLocation = locationService.getSelectedLocation()
        finalLocationId = selectedLocation?.id || null
        if (finalLocationId) {
          console.log('📍 locationId obtido automaticamente do sessionStorage:', finalLocationId)
        } else {
          console.log('⚠️ Nenhum locationId encontrado no sessionStorage')
        }
      } catch (error) {
        console.warn('Erro ao obter locationId do sessionStorage:', error)
      }
    } else {
      console.log('📍 locationId fornecido explicitamente:', finalLocationId)
    }

    console.log('productService.getProducts chamado com filtros:', filters)
    console.log('Enviando requisição para API com parâmetros:', {
      categoryId,
      categoryIds,
      brands: brands?.join(','),
      minPrice,
      maxPrice,
      page,
      limit,
      sortBy,
      search,
      filters: dynamicFilters,
      locationId: finalLocationId
    })

    try {
      console.log(`Fazendo requisição GET para ${API_URL}/products`)
      console.log('Parâmetro search:', search)
      console.log('Parâmetro filters (dinâmicos):', dynamicFilters)
      console.log('Parâmetro categoryIds:', categoryIds)

      // Build the parameters object
      const params = {
        categoryId,
        categoryIds: categoryIds?.join(','),
        brands: brands?.join(','),
        minPrice,
        maxPrice,
        page,
        limit,
        sortBy,
        search,  // Including the search parameter in the request
        inStockOnly,
        locationId: finalLocationId  // Including locationId for dynamic price calculation (obtained from sessionStorage if not provided)
      };

      // Adds dynamic filters if they exist
      // The backend expects to receive them as an array: filters[]=id1&filters[]=id2
      if (dynamicFilters && Array.isArray(dynamicFilters) && dynamicFilters.length > 0) {
        params.filters = dynamicFilters;
        console.log('✅ Filtros dinâmicos adicionados aos parâmetros:', dynamicFilters)
      }

      console.log('Parâmetros completos da requisição:', params);

      // Use the api instance instead of axios directly
      const response = await api.get(`/products`, { params });

      console.log('Resposta da API:', response.data);
      console.log('Número de itens retornados:', response.data?.items?.length || 0);
      return response.data
    } catch (error) {
      console.error('Erro ao buscar produtos:', error)
      console.error('Detalhes do erro:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      })
      // Returns an empty object with an empty items array to avoid errors
      return { items: [] }
    }
  },

  async getBrands() {
    const response = await axios.get(`${API_URL}/brands`)
    return response.data
  },

  async getBrandsByCategory(categoryId) {
    const response = await axios.get(`${API_URL}/products/brands-by-category/${categoryId}`)
    return response.data
  },

  async getPaginatedProducts(params = {}) {
    try {
      console.log('Buscando produtos paginados com parâmetros:', params)
      const response = await axios.get(`${API_URL}/products/paginated`, { params })
      return response.data
    } catch (error) {
      console.error('Error fetching paginated products:', error)
      throw error
    }
  },

  /**
   * Maximum list price within the selection (API: MAX(product.price)), fast.
   * Only with PRICE_MAX_USE_EXACT_DYNAMIC in the API + JWT + locationId does the backend compute the ceiling by Neovation price (slow).
   */
  async getMaxPrice(categoryId = null, brands = [], locationId = null, categoryIds = null) {
    try {
      const params = {};
      if (categoryIds && categoryIds.length > 0) {
        params.categoryIds = categoryIds.join(',')
      } else if (categoryId) {
        params.categoryId = categoryId
      }
      if (brands && brands.length > 0) params.brands = brands.join(',');
      if (locationId) params.locationId = locationId;

      const response = await api.get('/products/max-price', { params });
      console.log('Max price response:', response.data);

      const raw = response.data?.maxPrice;
      const num = raw != null ? Number(raw) : NaN;
      if (Number.isFinite(num) && num > 0) return num;
      return 0;
    } catch (error) {
      console.error('Error fetching max price:', error);
      return 0;
    }
  },

  /**
   * Estimated slider ceiling: MAX(list price) within the selection + headroom (tends to go a bit higher),
   * rounded in steps — fast (1 query on the API).
   */
  async getSliderPriceCeil(categoryId = null, brands = [], locationId = null, categoryIds = null) {
    const STEP = 500;
    const HEADROOM = 1.1;
    const MIN_CEIL = 1000;
    const FALLBACK_CATALOG_CEIL = 100000;

    let base = await this.getMaxPrice(categoryId, brands, locationId, categoryIds);
    if (!Number.isFinite(base) || base <= 0) {
      base = await this.getMaxPrice(null, [], locationId, null);
    }
    if (!Number.isFinite(base) || base <= 0) {
      return FALLBACK_CATALOG_CEIL;
    }
    const withHeadroom = base * HEADROOM;
    const rounded = Math.ceil(withHeadroom / STEP) * STEP;
    return Math.max(rounded, MIN_CEIL);
  },

  async getProductById(id, locationId = null, unit = null) {
    try {
      // If locationId was not provided, try to obtain it from sessionStorage
      let finalLocationId = locationId
      if (!finalLocationId) {
        try {
          const selectedLocation = locationService.getSelectedLocation()
          finalLocationId = selectedLocation?.id || null
          if (finalLocationId) {
            console.log('📍 getProductById - locationId obtido automaticamente do sessionStorage:', finalLocationId)
          }
        } catch (error) {
          console.warn('Erro ao obter locationId do sessionStorage:', error)
        }
      }

      const params = {};
      if (finalLocationId) params.locationId = finalLocationId;
      if (unit) params.unit = unit;

      // Uses 'api' instead of 'axios' so the token is sent automatically
      const response = await api.get(`/products/${id}`, { params })
      const product = response.data

      // If the product has a FoxPro code, fetch the images
      if (product.foxpro_code) {
        try {
          const imagesResponse = await this.getProductImages(product.foxpro_code);
          if (imagesResponse && imagesResponse.length > 0) {
            // Sets the first image as the main image if none is defined
            if (!product.image) {
              product.image = imagesResponse[0];
            }
            // Adds all images to the images array
            product.images = imagesResponse;
          }
        } catch (error) {
          console.error('Erro ao buscar imagens do produto:', error);
        }
      }

      return product
    } catch (error) {
      console.error('Error fetching product:', error)
      throw error
    }
  },

  async getStockByLocation(productId) {
    try {
      const response = await axios.get(`${API_URL}/products/${productId}/stock-by-location`)
      return response.data
    } catch (error) {
      console.error('Error fetching stock by location:', error)
      return {
        locations: [],
        foxproCode: null,
        unitFactor: 1.0
      }
    }
  },

  /**
   * Fetches all images of a product by foxpro_code
   * @param {string} foxproCode - FoxPro code of the product
   * @returns {Promise<string[]>} - Array with the image paths
   */
  async getProductImages(foxproCode) {
    if (!foxproCode) {
      return [];
    }

    try {
      const response = await axios.get(`${API_URL}/products/images/${foxproCode}`);
      return response.data.images || [];
    } catch (error) {
      console.error(`Erro ao buscar imagens para o produto com código ${foxproCode}:`, error);
      return [];
    }
  },

  async getProductDetails(id, locationId = null, unit = null) {
    try {
      // Reuses the getProductById method to obtain the product details
      // This ensures that the images are also loaded
      // If locationId was not provided, try to obtain it from sessionStorage
      let finalLocationId = locationId
      if (!finalLocationId) {
        try {
          const selectedLocation = locationService.getSelectedLocation()
          finalLocationId = selectedLocation?.id || null
          if (finalLocationId) {
            console.log('📍 getProductDetails - locationId obtido automaticamente do sessionStorage:', finalLocationId)
          } else {
            console.log('⚠️ getProductDetails - Nenhum locationId encontrado no sessionStorage')
          }
        } catch (error) {
          console.warn('Erro ao obter locationId do sessionStorage em getProductDetails:', error)
        }
      } else {
        console.log('📍 getProductDetails - locationId fornecido explicitamente:', finalLocationId)
      }
      return await this.getProductById(id, finalLocationId, unit);
    } catch (error) {
      console.error('Error fetching product details:', error)
      throw error
    }
  },

  async updateProduct(id, productData) {
    try {
      // List of known properties of the Product model in the backend
      const knownProperties = [
        'id', 'name', 'price', 'description_en', 'description_pt', 'description_fr',
        'technical_description_en', 'technical_description_pt', 'technical_description_fr',
        'image', 'images', 'foxpro_code', 'characteristics', 'isActive', 'categoryId', 'brandId',
        'createdAt', 'updatedAt'
      ]

      // Filter only known properties to avoid errors in the backend
      const filteredProductData = {}
      for (const key in productData) {
        if (knownProperties.includes(key)) {
          filteredProductData[key] = productData[key]
        }
      }

      console.log(`Enviando requisição PATCH para ${API_URL}/products/${id} com dados filtrados:`, filteredProductData)
      const response = await axios.patch(`${API_URL}/products/${id}`, filteredProductData)
      console.log('Resposta da API após atualização do produto:', response.data)
      return response.data
    } catch (error) {
      console.error('Error updating product:', error)
      throw error
    }
  }
}








